// The editable fields of a platform (sub-account) on its card, and the PUT /subaccount/:id payload built from them.
// The payloads are the ones of the former edit pages (createPlatform/Site.vue, Arbitrage.vue, Directlink.vue): the
// saved record goes first, then the form values on top, so the backend gets every field it expects.

export const PAYMENT_OPTIONS = [
  { title: "CPL", value: 1 },
  { title: "Revshare", value: 0 },
];

export const CATEGORIES = [
  { title: "Adult", value: 1 },
  { title: "Mainstream", value: 2 },
];

// Picking every category to disable turns into this one
export const ALL_CATEGORIES = { name_en: "All", name_ru: "Все", id: 0 };

export function allCategoriesDisabled(selected) {
  return Boolean(selected && selected[0] && selected[0].id === ALL_CATEGORIES.id);
}

export const REDIRECT_MODES = [
  { title: "platform.arbitrage.window_accept", value: 1 },
  { title: "platform.arbitrage.redirect", value: 0 },
  { title: "platform.arbitrage.on_site_click", value: 2 },
];

export const ROTATION_MODES = [
  { title: "platform.arbitrage.random", value: "random" },
  { title: "platform.arbitrage.new_to_old", value: "newest-to-oldest" },
];

const SITE_POSTBACK_FORMATS = [
  { title: "Push", id: 0, value: "push" },
  { title: "InPage", id: 1, value: "in_page" },
  { title: "OnClick", id: 2, value: "on_click" },
];

const ARBITRAGE_POSTBACK_FORMATS = [...SITE_POSTBACK_FORMATS, { title: "TB Leads", id: 3, value: "tb_leads" }];

const POSTBACK_FLAGS = {
  push: "enable_postback_push_revshare",
  in_page: "enable_postback_in_page_revshare",
  on_click: "enable_postback_on_click_revshare",
  tb_leads: "enable_postback_tb_leads",
};

export const KIND_SITE = "site";
export const KIND_LANDING = "landing";
export const KIND_ARBITRAGE = "arbitrage";
export const KIND_DIRECTLINK = "directlink";

// `site` of a sub-account: 0 arbitrage (and direct links), 1 own site, 2 landing.
export function platformKind(subaccount) {
  if (subaccount.direct_link) return KIND_DIRECTLINK;
  if (subaccount.site === 1) return KIND_SITE;
  if (subaccount.site === 2) return KIND_LANDING;
  return KIND_ARBITRAGE;
}

export function isSiteKind(kind) {
  return kind === KIND_SITE || kind === KIND_LANDING;
}

export function postbackFormatList(kind) {
  return isSiteKind(kind) ? SITE_POSTBACK_FORMATS : ARBITRAGE_POSTBACK_FORMATS;
}

export function postbackFormatsOf(subaccount, kind) {
  return postbackFormatList(kind).filter((format) => subaccount[POSTBACK_FLAGS[format.value]]);
}

function hasPostbackFormat(form, format) {
  return form.postback_format.some((el) => el.value === format);
}

export function paymentModelOf(value) {
  return value == 1 ? PAYMENT_OPTIONS[0] : PAYMENT_OPTIONS[1];
}

// Options are the lists loaded from the API (see platformOptions.js).
export function formFromSubaccount(subaccount, kind, options) {
  const common = {
    name: subaccount.name,
    payment_model: paymentModelOf(subaccount.payment_model),
    schedule_days: subaccount.schedule_days || [],
    postback_url: subaccount.postback_url || "",
    postback_format: postbackFormatsOf(subaccount, kind),
    mailing_frequency: options.mailings.find((el) => el.value === subaccount.mailing_frequency) || { title: "Custom", value: "custom" },
    push_send_interval: options.pushIntervals.find((el) => el.value === subaccount.push_send_interval) || options.pushIntervals[0],
    push_delay_interval: options.pushDelays.find((el) => el.value === subaccount.push_delay_interval) || options.pushDelays[0],
    feedsSales: subaccount.deny_sell_to_feeds,
  };

  if (isSiteKind(kind)) {
    return {
      ...common,
      url: subaccount.url,
      disabledCategory: options.disabledCategories.filter((item) => (subaccount.disabled_category_ids || []).includes(item.id)),
      category_id: CATEGORIES.find((el) => el.value === subaccount.category_id),
      adult: Boolean(subaccount.adult),
    };
  }

  return {
    ...common,
    complexity: subaccount.complexity,
    redirect_type: REDIRECT_MODES.find((el) => el.value === subaccount.complexity_redirect),
    tb_subscription: subaccount.tb_subscription,
    tb_complexity: subaccount.tb_complexity,
    tb_subscription_url: subaccount.tb_subscription_url || "",
    tb_complexity_url: subaccount.tb_complexity_url || "",
    lands: subaccount.lands || [],
    fullscreen: subaccount.fullscreen,
    enable_in_page_ads: subaccount.enable_in_page_ads,
    enable_onclick_ads: subaccount.enable_onclick_ads,
    redirect_to_actual_domain: subaccount.redirect_to_actual_domain,
    domain_rotation: ROTATION_MODES.find((el) => el.value === subaccount.redirect_to_personal_domain_settings?.strategy) || ROTATION_MODES[0],
  };
}

function sitePayload(subaccount, form) {
  const saved = {
    name: subaccount.name,
    schedule_days: subaccount.schedule_days,
    complexity: +subaccount.complexity,
    tb_subscription: subaccount.tb_subscription,
    tb_complexity: subaccount.tb_complexity,
    tb_subscription_url: subaccount.tb_subscription_url,
    tb_complexity_url: subaccount.tb_complexity_url,
    mailing_frequency: subaccount.mailing_frequency,
    push_send_interval: subaccount.push_send_interval || null,
    push_delay_interval: subaccount.push_delay_interval || 0,
    adult: +subaccount.adult,
    fullscreen: +subaccount.fullscreen,
    lands: subaccount.lands || [],
    postback_url: subaccount.postback_url,
  };
  return Object.assign(saved, {
    name: form.name,
    schedule_days: form.schedule_days,
    disabled_category_ids: form.disabledCategory.map((el) => el.id) || [],
    payment_model: +form.payment_model.value,
    mailing_frequency: form.mailing_frequency?.value || "standart",
    push_send_interval: form.push_send_interval?.value || null,
    push_delay_interval: form.push_delay_interval?.value || 0,
    postback_url: form.postback_url,
    adult: +form.adult,
    deny_sell_to_feeds: form.payment_model.value === 0 ? +form.feedsSales : 0,
    enable_postback_in_page_revshare: hasPostbackFormat(form, "in_page"),
    enable_postback_on_click_revshare: hasPostbackFormat(form, "on_click"),
    enable_postback_push_revshare: hasPostbackFormat(form, "push"),
  });
}

// The arbitrage form values, shared by arbitrage and direct link platforms.
function arbitrageFormPayload(form) {
  return {
    site: 0,
    name: form.name,
    payment_model: form.payment_model.value,
    complexity_redirect: form.redirect_type.value,
    mailing_frequency: form.mailing_frequency.value,
    tb_subscription: form.tb_subscription,
    tb_complexity: form.tb_complexity,
    complexity: form.complexity,
    tb_subscription_url: form.tb_subscription === "user" ? form.tb_subscription_url : undefined,
    tb_complexity_url: form.tb_complexity === "user" ? form.tb_complexity_url : undefined,
    lands: form.lands,
    enable_onclick_ads: form.enable_onclick_ads,
    enable_in_page_ads: form.enable_in_page_ads,
    redirect_to_actual_domain: +form.redirect_to_actual_domain,
    postback_url: form.postback_url,
    fullscreen: +form.fullscreen,
    deny_sell_to_feeds: form.payment_model.value === 0 ? +form.feedsSales : 0,
    redirect_to_personal_domain_settings: {
      active: form.redirect_to_actual_domain,
      strategy: form.redirect_to_actual_domain ? form.domain_rotation.value : undefined,
    },
    enable_postback_in_page_revshare: hasPostbackFormat(form, "in_page"),
    enable_postback_on_click_revshare: hasPostbackFormat(form, "on_click"),
    enable_postback_push_revshare: hasPostbackFormat(form, "push"),
    enable_postback_tb_leads: hasPostbackFormat(form, "tb_leads"),
  };
}

// POST /subaccount of the create form of an arbitrage platform
export function newArbitragePayload(form) {
  return Object.assign(arbitrageFormPayload(form), {
    schedule_days: form.schedule_days,
    push_send_interval: form.push_send_interval?.value,
    push_delay_interval: form.push_delay_interval?.value,
  });
}

// The saved values every arbitrage-like payload starts from.
function savedArbitrageFields(subaccount) {
  return {
    name: subaccount.name,
    complexity: +subaccount.complexity,
    tb_subscription: subaccount.tb_subscription,
    tb_complexity: subaccount.tb_complexity,
    tb_subscription_url: subaccount.tb_subscription_url,
    tb_complexity_url: subaccount.tb_complexity_url,
    mailing_frequency: subaccount.mailing_frequency,
    adult: +subaccount.adult,
    fullscreen: +subaccount.fullscreen,
    lands: subaccount.lands || [],
    postback_url: subaccount.postback_url,
  };
}

function arbitragePayload(subaccount, form) {
  const saved = {
    ...savedArbitrageFields(subaccount),
    schedule_days: subaccount.schedule_days,
    push_send_interval: subaccount.push_send_interval,
    push_delay_interval: subaccount.push_delay_interval,
  };
  return Object.assign(saved, arbitrageFormPayload(form), {
    schedule_days: form.schedule_days,
    push_send_interval: form.push_send_interval?.value,
    push_delay_interval: form.push_delay_interval?.value,
  });
}

// Direct links have no mailing settings on the card: the saved frequency goes back as it is.
function directlinkPayload(subaccount, form) {
  const saved = {
    ...savedArbitrageFields(subaccount),
    is_direct_link: subaccount.direct_link || false,
    traffic_type: subaccount.traffic_type || null,
  };
  return Object.assign(saved, arbitrageFormPayload(form), { mailing_frequency: subaccount.mailing_frequency });
}

export function subaccountPayload(subaccount, kind, form) {
  if (isSiteKind(kind)) return sitePayload(subaccount, form);
  if (kind === KIND_DIRECTLINK) return directlinkPayload(subaccount, form);
  return arbitragePayload(subaccount, form);
}

// The hours of a schedule by day. The schedule grid sends every hour when nothing is saved yet, which is what an
// empty schedule means, so both compare equal and opening the mailing tab does not look like a change.
function scheduleHours(schedule) {
  const days = Array.from({ length: 7 }, () => Array.from({ length: 24 }, (_, hour) => hour));
  if (!schedule || !schedule.length) return days;
  (schedule || []).forEach((day) => {
    days[day.value - 1] = [...(day[`schedule_day_${day.value}`] || [])].sort((a, b) => a - b);
  });
  return days;
}

// What the dirty check compares: the form without the option objects' extra fields.
export function formSnapshot(form) {
  if (!form) return "";
  return JSON.stringify({ ...form, schedule_days: scheduleHours(form.schedule_days) });
}
